import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';
import CountrySelect from '@/components/onboarding/location/CountrySelect';
import StateSelect from '@/components/onboarding/location/StateSelect';
import CitySelect from '@/components/onboarding/location/CitySelect';

interface EditableLocationSectionProps {
  formData: {
    city?: string;
    state_code?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
  };
  updateLocalData: (field: string, value: any) => void;
  syncWithAuthContext: (updatedFields: Record<string, any>) => Promise<void>;
  userId: string;
}

const EditableLocationSection: React.FC<EditableLocationSectionProps> = ({
  formData,
  updateLocalData,
  syncWithAuthContext,
  userId
}) => {
  const [isSaving, setIsSaving] = useState<string | null>(null);

  const handleCountryChange = async (value: string) => {
    // Update local state and clear state/city
    updateLocalData('country', value);
    updateLocalData('state_code', '');
    updateLocalData('city', '');
    
    if (!userId) return;
    
    setIsSaving('country');
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ country: value, state_code: null, city: null })
        .eq('id', userId);

      if (error) throw error;

      await syncWithAuthContext({ country: value, state_code: '', city: '' });
      toast.success('País atualizado', { duration: 1500 });
      debugLogger.debug('Location', 'Country updated:', value);
    } catch (error) {
      debugLogger.error('Location', 'Error updating country:', error);
      toast.error('Erro ao salvar país');
    } finally {
      setIsSaving(null);
    }
  };

  const handleStateChange = async (value: string) => {
    // Update local state and clear city
    updateLocalData('state_code', value);
    updateLocalData('city', '');
    
    if (!userId) return;
    
    setIsSaving('state_code');
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ state_code: value, city: null })
        .eq('id', userId);

      if (error) throw error;

      await syncWithAuthContext({ state_code: value, city: '' });
      toast.success('Estado atualizado', { duration: 1500 });
      debugLogger.debug('Location', 'State updated:', value);
    } catch (error) {
      debugLogger.error('Location', 'Error updating state:', error);
      toast.error('Erro ao salvar estado');
    } finally {
      setIsSaving(null);
    }
  };

  const handleCityChange = async (value: string) => {
    // Update local state
    updateLocalData('city', value);
    
    if (!userId || !value) return;
    
    setIsSaving('city');
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ city: value })
        .eq('id', userId);

      if (error) throw error;

      await syncWithAuthContext({ city: value });
      toast.success('Cidade atualizada', { duration: 1500 });
      debugLogger.debug('Location', 'City updated:', value);
    } catch (error) {
      debugLogger.error('Location', 'Error updating city:', error);
      toast.error('Erro ao salvar cidade');
    } finally {
      setIsSaving(null);
    }
  };

  const currentCountry = formData.country || 'BR';

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <MapPin className="h-4 w-4 text-green-400" />
            </div>
            <h3 className="text-white font-medium text-lg">Localização</h3>
          </div>
          {isSaving && (
            <span className="text-xs text-white/40">Salvando...</span>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-white/80">
              País
            </label>
            <CountrySelect
              value={currentCountry}
              onChange={handleCountryChange}
              placeholder="Selecione o país"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-white/80">
              Estado
            </label>
            <StateSelect
              value={formData.state_code || ''}
              onChange={handleStateChange}
              countryCode={currentCountry}
              placeholder="Selecione o estado"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-white/80">
              Cidade
            </label>
            <CitySelect
              value={formData.city || ''}
              onChange={handleCityChange}
              countryCode={currentCountry}
              stateCode={formData.state_code || null}
              placeholder="Selecione sua cidade"
            />
          </div>

          <p className="text-xs text-white/50 mt-3">
            📍 Use sua cidade base. Essa informação ajuda nas sugestões de perfis 
            e não muda automaticamente quando você estiver viajando.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditableLocationSection;
